/*
 * Workspace settings affect a client; agency settings affect everyone in the agency and are shown only
 * to the people the API lets manage them; account settings affect one person. Campaign settings live
 * on the campaign page; this catalogue includes only workspace campaign defaults.
 * See docs/2026-09-25-campaigns-list-and-page.md.
 */

type SettingsScope = "workspace" | "agency" | "account";

export type SettingsSlug =
  | "drafting"
  | "campaigns"
  | "addresses"
  | "email-bison"
  | "hubspot"
  | "brief-import"
  | "inbox"
  | "invitations"
  | "tags"
  | "assignment"
  | "people"
  | "workspaces"
  | "profile"
  | "appearance"
  | "shortcuts"
  | "connections";

export type SettingsSection = {
  readonly slug: SettingsSlug;
  readonly scope: SettingsScope;

  readonly title: string;
  /**
   * A page of its own that Settings lists rather than draws: its address stays its own, since it
   * must open without the workspace list (Connected apps, LEA-498).
   */
  readonly href?: string;
};

/** Order is the order they are listed in; scope decides which group they are listed under. */
export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  { slug: "drafting", scope: "workspace", title: "Drafting" },
  { slug: "campaigns", scope: "workspace", title: "Campaign defaults" },
  { slug: "addresses", scope: "workspace", title: "Our addresses" },
  { slug: "email-bison", scope: "workspace", title: "Email Bison" },
  { slug: "hubspot", scope: "workspace", title: "HubSpot" },
  { slug: "brief-import", scope: "workspace", title: "Brief import" },
  // Named for what it sets: "Inbox" was also the rail's section and the panel's home (LEA-501).
  { slug: "inbox", scope: "workspace", title: "Seen by" },
  { slug: "tags", scope: "workspace", title: "Tags" },
  // Listed only once the API has said this person may set up assignment here (`settings/assignment.tsx`).
  { slug: "invitations", scope: "workspace", title: "Invitations" },
  { slug: "assignment", scope: "workspace", title: "Assignment" },
  { slug: "people", scope: "agency", title: "People" },
  // Listed only once the API has said this person may add and remove workspaces (`settings/workspaces.tsx`).
  { slug: "workspaces", scope: "agency", title: "Workspaces" },
  { slug: "profile", scope: "account", title: "Profile" },
  { slug: "appearance", scope: "account", title: "Appearance" },
  { slug: "shortcuts", scope: "account", title: "Keyboard shortcuts" },
  { slug: "connections", scope: "account", title: "Connected apps", href: "/account/connections" },
];

export const DEFAULT_SETTINGS_SECTION: SettingsSlug = "drafting";

/**
 * The section a URL names, or null when it names none this app has. Null rather than a silent
 * fallback: a stale bookmark should be able to say so rather than quietly showing something else.
 */
export function settingsSectionFor(slug: string | null | undefined): SettingsSection | null {
  if (slug === null || slug === undefined || slug === "") return null;
  const named = Object.hasOwn(RENAMED_SECTIONS, slug) ? (RENAMED_SECTIONS[slug] ?? slug) : slug;
  return SETTINGS_SECTIONS.find((section) => section.slug === named) ?? null;
}

/** Old names of a section that kept its place, so an old bookmark opens the section it became. */
const RENAMED_SECTIONS: Readonly<Record<string, SettingsSlug>> = {
  // Routing became Assignment (LEA-477): the ordered rules gave way to a mode.
  routing: "assignment",
};

/* Legacy sign-off bookmarks redirect to campaigns, where sign-offs are now configured. */
export type MovedSettingsSection = "campaigns";
const MOVED_SECTIONS: Readonly<Record<string, MovedSettingsSection>> = { "sign-off": "campaigns" };

/** Where a section that left the settings area now lives, or null when it did not move. */
export function movedSettingsSection(slug: string | null | undefined): MovedSettingsSection | null {
  if (slug === null || slug === undefined) return null;
  return Object.hasOwn(MOVED_SECTIONS, slug) ? (MOVED_SECTIONS[slug] ?? null) : null;
}

/*
 * Where a section lives (LEA-501). A workspace setting changes what the app does for one client, so
 * its address names the client. Agency and account settings belong to no client, and their routes
 * name none (`/api/work/people`, `/api/work/workspaces`); under a client's address, adding a client
 * happened inside another client's settings.
 */
export type DetachedScope = Exclude<SettingsScope, "workspace">;

/** The address of an agency or account section; null for a workspace section, which needs a client. */
export function detachedSettingsPath(section: SettingsSection): string | null {
  if (section.scope === "workspace") return null;
  return section.href ?? `/settings/${section.scope}/${section.slug}`;
}

/**
 * The section a detached address names: only a section of that scope, so `/settings/agency/drafting`
 * is no page rather than a workspace setting with no workspace.
 */
export function detachedSettingsSection(
  scope: DetachedScope,
  slug: string | null | undefined,
): SettingsSection | null {
  const section = settingsSectionFor(slug);
  return section?.scope === scope ? section : null;
}

/** The detached address of a section named by its slug; null for a workspace section. */
export function detachedSlugPath(slug: SettingsSlug): string | null {
  const section = settingsSectionFor(slug);
  return section ? detachedSettingsPath(section) : null;
}

/**
 * Where an old address under a workspace now opens: the detached address of an agency or account
 * section, or null when the slug names a workspace section (or none) and the address stands.
 */
export function settingsRedirect(slug: string | null | undefined): string | null {
  const section = settingsSectionFor(slug);
  return section ? detachedSettingsPath(section) : null;
}

export type SettingsGroup = {
  readonly scope: SettingsScope;
  readonly sections: readonly SettingsSection[];
};

/**
 * The groups in the order they are shown. The workspace's own group is labelled with the client's
 * name by the caller, because only the caller knows it.
 */
export function settingsGroups(): readonly SettingsGroup[] {
  const scopes: readonly SettingsScope[] = ["workspace", "agency", "account"];
  return scopes.map((scope) => ({
    scope,
    sections: SETTINGS_SECTIONS.filter((section) => section.scope === scope),
  }));
}

/* Keep the held campaign's fact focus in the URL so navigation, reloads and shared links retain it. */
export type StandingAnswerFocus = { readonly key: string; readonly campaignId: string | null };

/** The query string that carries a focus, without the leading `?`. */
export function standingAnswerQuery(focus: StandingAnswerFocus): string {
  const params = new URLSearchParams({ key: focus.key });
  if (focus.campaignId !== null) params.set("campaign", focus.campaignId);
  return params.toString();
}

/**
 * The focus a URL carries, or null when it carries none. A campaign without a key is not a focus:
 * the key is the fact being answered, and without it there is nothing to open.
 */
export function parseStandingAnswerFocus(search: string): StandingAnswerFocus | null {
  const params = new URLSearchParams(search);
  const key = params.get("key");
  if (key === null || key === "") return null;
  const campaign = params.get("campaign");
  return { key, campaignId: campaign === null || campaign === "" ? null : campaign };
}
